import { X, Plus, Check, ArrowDownUp, Minus, Link } from "lucide-react";
import { Button } from "@/components/ui/button";
import { ScrollArea } from "@/components/ui/scroll-area";
import type { DiagnosisAction } from "./types";

interface DiagnosisTimelineProps {
  code: string;
  description: string;
  timeline: DiagnosisAction[];
  onClose: () => void;
  onEvidenceClick?: (quote: { text: string; fileIdentifier: string }) => void;
}

const ACTION_CONFIG: Record<
  DiagnosisAction["actionType"],
  { icon: typeof Plus; label: string; color: string; bgColor: string; borderColor: string }
> = {
  ADD: {
    icon: Plus,
    label: "ADD",
    color: "text-green-400",
    bgColor: "bg-green-950",
    borderColor: "border-green-500",
  },
  KEEP: {
    icon: Check,
    label: "KEEP",
    color: "text-green-400",
    bgColor: "bg-green-950",
    borderColor: "border-green-500",
  },
  DROP: {
    icon: Minus,
    label: "DROP",
    color: "text-red-400",
    bgColor: "bg-red-950",
    borderColor: "border-red-500",
  },
  REORDER: {
    icon: ArrowDownUp,
    label: "REORDER",
    color: "text-blue-400",
    bgColor: "bg-blue-950",
    borderColor: "border-blue-500",
  },
  PAIR_ASTERISK: {
    icon: Link,
    label: "PAIR",
    color: "text-purple-400",
    bgColor: "bg-purple-950",
    borderColor: "border-purple-500",
  },
};

function formatSource(source: string): string {
  return source.replace(":", " · ");
}

export function DiagnosisTimeline({ code, description, timeline, onClose, onEvidenceClick }: DiagnosisTimelineProps) {
  return (
    <div className="flex flex-col h-full border-l border-purple-700 bg-gray-950">
      {/* Header */}
      <div className="flex items-center justify-between px-3 py-2 bg-purple-950 border-b border-purple-800">
        <div className="min-w-0">
          <span className="text-sm font-semibold text-purple-200">Timeline: </span>
          <span className="text-sm font-mono font-bold text-purple-100">{code}</span>
        </div>
        <Button
          size="sm"
          variant="ghost"
          className="h-6 w-6 p-0 text-purple-300 hover:text-purple-100"
          onClick={onClose}
          aria-label="Close timeline"
        >
          <X className="h-4 w-4" />
        </Button>
      </div>

      {/* Description */}
      <div className="px-3 py-1.5 text-xs text-gray-400 border-b border-gray-800">{description}</div>

      {/* Steps */}
      <ScrollArea className="flex-1">
        <div className="p-3">
          {timeline.map((action, index) => {
            const config = ACTION_CONFIG[action.actionType];
            const Icon = config.icon;
            const isLast = index === timeline.length - 1;

            return (
              <div key={index} className="flex gap-3 mb-0">
                {/* Vertical line + icon */}
                <div className="flex flex-col items-center">
                  <div
                    className={`w-6 h-6 rounded-full ${config.bgColor} border-2 ${config.borderColor} flex items-center justify-center flex-shrink-0`}
                  >
                    <Icon className={`h-3 w-3 ${config.color}`} />
                  </div>
                  {!isLast && <div className="w-0.5 flex-1 bg-gray-700 mt-1" />}
                </div>

                {/* Content */}
                <div className="flex-1 pb-4 min-w-0">
                  <div className={`text-xs font-bold ${config.color}`}>{config.label}</div>
                  <div className="text-[10px] text-gray-500 mt-0.5">{formatSource(action.source)}</div>

                  {action.leadTerm && (
                    <div className="text-xs text-gray-300 mt-1">Lead term: &ldquo;{action.leadTerm}&rdquo;</div>
                  )}

                  {action.reason && <div className="text-xs text-gray-400 mt-1 italic">{action.reason}</div>}

                  {action.metadata?.pairedWithCode && (
                    <div className="text-xs text-purple-300 mt-1">Paired with: {action.metadata.pairedWithCode}</div>
                  )}

                  {action.metadata?.replacedByCode && (
                    <div className="text-xs text-red-300 mt-1">Replaced by: {action.metadata.replacedByCode}</div>
                  )}

                  {action.metadata?.replacesCode && (
                    <div className="text-xs text-green-300 mt-1">Replaces: {action.metadata.replacesCode}</div>
                  )}

                  {action.metadata?.orderAfter && (
                    <div className="text-xs text-blue-300 mt-1">
                      New order: {action.metadata.orderAfter.join(" → ")}
                    </div>
                  )}

                  {/* Evidence quotes */}
                  {action.evidenceQuotes?.map((quote, qi) => (
                    <button
                      key={qi}
                      className="mt-2 w-full text-left bg-yellow-950 border border-yellow-800 rounded px-2 py-1.5 text-xs text-yellow-200 hover:bg-yellow-900 hover:border-yellow-600 transition-colors cursor-pointer flex items-start gap-1.5"
                      onClick={() =>
                        onEvidenceClick?.({
                          text: quote.text,
                          fileIdentifier: quote.fileIdentifier,
                        })
                      }
                    >
                      <span className="text-yellow-500 flex-shrink-0 mt-0.5">✎</span>
                      <span className="line-clamp-2">&ldquo;{quote.text}&rdquo;</span>
                    </button>
                  ))}
                </div>
              </div>
            );
          })}
        </div>
      </ScrollArea>
    </div>
  );
}
